import React, { useMemo } from 'react'

import { Table, TableColumnsType } from 'antd'

interface MetricsSummaryProps {
  data: {
    ClusterDiff?: Array<{
      Key: string
      Metrics: Array<{
        Tag: string
        Aggregation: {
          Max?: {
            ClusterA: string
            ClusterB: string
            Diff: string
          }
          Avg?: {
            ClusterA: string
            ClusterB: string
            Diff: string
          }
        }
      }>
    }>
  }
}

interface MetricValue {
  clusterA: number
  clusterB: number
  diffPercent: number
  formatted: { text: string; diffPercent: number }
}

// 从 ClusterDiff 中提取指标数据
function extractMetrics(
  clusterDiff: MetricsSummaryProps['data']['ClusterDiff']
): Record<string, MetricValue> {
  const result: Record<string, MetricValue> = {}

  if (!clusterDiff || !Array.isArray(clusterDiff)) {
    return result
  }

  // 映射关系：Key -> 输出字段名 -> 聚合类型
  const avgMaxMapping: Record<
    string,
    { max?: string; avg?: string; tag: string }
  > = {
    latency_avg_all: { avg: 'duration-avg', tag: 'All' },
    latency_p999_all: {
      max: 'duration-p999-max',
      avg: 'duration-p999-avg',
      tag: 'All'
    },
    cps: { max: 'cps-max', avg: 'cps-avg', tag: 'OK' },
    cpu_tidb: { max: 'tidb-max', avg: 'tidb-avg', tag: 'All' },
    cpu_tikv: { max: 'tikv-max', avg: 'tikv-avg', tag: 'All' },
    cpu_tiflash: { max: 'tiflash-max', avg: 'tiflash-avg', tag: 'All' }
  }

  for (const item of clusterDiff) {
    const { Key, Metrics } = item

    if (avgMaxMapping[Key]) {
      const mapping = avgMaxMapping[Key]
      const metric = Metrics.find((m) => m.Tag === mapping.tag)

      // Max 值
      if (mapping.max && metric?.Aggregation?.Max) {
        const { ClusterA, ClusterB, Diff } = metric.Aggregation.Max
        const clusterAVal = parseFloat(ClusterA)
        const clusterBVal = parseFloat(ClusterB)
        const diffPercent = parseFloat(Diff)

        result[mapping.max] = {
          clusterA: clusterAVal,
          clusterB: clusterBVal,
          diffPercent,
          formatted: formatMetricValue(clusterAVal, clusterBVal, diffPercent)
        }
      }

      // Avg 值
      if (mapping.avg && metric?.Aggregation?.Avg) {
        const { ClusterA, ClusterB, Diff } = metric.Aggregation.Avg
        const clusterAVal = parseFloat(ClusterA)
        const clusterBVal = parseFloat(ClusterB)
        const diffPercent = parseFloat(Diff)

        result[mapping.avg] = {
          clusterA: clusterAVal,
          clusterB: clusterBVal,
          diffPercent,
          formatted: formatMetricValue(clusterAVal, clusterBVal, diffPercent)
        }
      }
    }
  }

  return result
}

// 格式化指标值显示：0.20 -> 0.17 (↓15.0%)
function formatMetricValue(
  clusterA: number,
  clusterB: number,
  diffPercent: number,
  unit?: string
): { text: string; diffPercent: number } {
  const arrow = diffPercent > 0 ? '↑' : diffPercent < 0 ? '↓' : '→'
  const sign = diffPercent > 0 ? '+' : ''
  const percentStr = `${sign}${diffPercent}%`
  const unitStr = unit ? ` ${unit}` : ''
  return {
    text: `${clusterA}${unitStr} → ${clusterB}${unitStr} (${arrow}${percentStr})`,
    diffPercent
  }
}

export const MetricsSummary: React.FC<MetricsSummaryProps> = ({ data }) => {
  const metrics = useMemo(
    () => extractMetrics(data.ClusterDiff),
    [data.ClusterDiff]
  )

  const renderMetricValue = (value: MetricValue | undefined) => {
    if (!value?.formatted) return '-'

    const { text, diffPercent } = value.formatted
    const arrow = diffPercent > 0 ? '↑' : diffPercent < 0 ? '↓' : '→'
    const color =
      diffPercent > 0 ? '#ff4d4f' : diffPercent < 0 ? '#52c41a' : undefined

    // 提取箭头和百分比部分，添加颜色
    const parts = text.split(`(${arrow}`)
    if (parts.length === 2) {
      const percentPart = parts[1].replace(')', '')
      return (
        <span>
          {parts[0]}
          <span style={{ color }}>
            ({arrow}
            {percentPart})
          </span>
        </span>
      )
    }

    return <span>{text}</span>
  }

  const renderAvgMaxMetric = (
    maxValue: MetricValue | undefined,
    avgValue: MetricValue | undefined
  ) => {
    if (!maxValue && !avgValue) return '-'

    return (
      <div style={{ lineHeight: '1.8' }}>
        {maxValue && (
          <div>
            <span>Max: </span>
            {renderMetricValue(maxValue)}
          </div>
        )}
        {avgValue && (
          <div>
            <span>Avg: </span>
            {renderMetricValue(avgValue)}
          </div>
        )}
      </div>
    )
  }

  const columns: TableColumnsType<Record<string, MetricValue>> = [
    {
      title: 'CPS',
      dataIndex: 'cps',
      key: 'cps',
      render: (_: any, record: Record<string, MetricValue>) =>
        renderAvgMaxMetric(record['cps-max'], record['cps-avg'])
    },
    {
      title: 'Duration P999',
      dataIndex: 'duration-p999',
      key: 'duration-p999',
      render: (_: any, record: Record<string, MetricValue>) =>
        renderAvgMaxMetric(
          record['duration-p999-max'],
          record['duration-p999-avg']
        )
    },
    {
      title: 'Duration Avg',
      dataIndex: 'duration-avg',
      key: 'duration-avg',
      render: (value: MetricValue) => renderMetricValue(value)
    },
    {
      title: 'TiDB CPU',
      key: 'tidb-cpu',
      render: (_: any, record: Record<string, MetricValue>) =>
        renderAvgMaxMetric(record['tidb-max'], record['tidb-avg'])
    },
    {
      title: 'TiKV CPU',
      key: 'tikv-cpu',
      render: (_: any, record: Record<string, MetricValue>) =>
        renderAvgMaxMetric(record['tikv-max'], record['tikv-avg'])
    },
    {
      title: 'TiFlash CPU',
      key: 'tiflash-cpu',
      render: (_: any, record: Record<string, MetricValue>) =>
        renderAvgMaxMetric(record['tiflash-max'], record['tiflash-avg'])
    }
  ]

  return (
    <section className='mb-6'>
      <h2 className='mb-4 text-2xl font-semibold'>Metrics Summary</h2>
      <Table
        columns={columns}
        dataSource={[metrics]}
        rowKey={() => 'metrics-summary'}
        pagination={false}
        bordered
        size='small'
      />
    </section>
  )
}
